import { useState } from "react";
import type { ResolvedPlatform } from "../../subscriptionView";
import { LinkButton } from "./LinkButton";
import { installToneBorderClass, installToneTextClass } from "../installTone";

export function InstallGuide({
  platforms,
  labels,
  defaultPlatformKey,
}: {
  platforms: ResolvedPlatform[];
  labels: Record<string, string>;
  defaultPlatformKey: string | null;
}) {
  const [selectedKey, setSelectedKey] = useState(
    platforms.find((p) => p.key === defaultPlatformKey)?.key ?? platforms[0]?.key,
  );
  const [pickerOpen, setPickerOpen] = useState(false);
  const [selectedAppIndex, setSelectedAppIndex] = useState(0);
  const [appPickerOpen, setAppPickerOpen] = useState(false);
  const platform = platforms.find((p) => p.key === selectedKey) ?? platforms[0];

  function selectPlatform(key: string) {
    setSelectedKey(key);
    setSelectedAppIndex(0);
    setPickerOpen(false);
  }

  if (!platform) return <p className="text-text-muted">{labels.noDevices}</p>;

  const app = platform.apps[selectedAppIndex] ?? platform.apps[0];

  return (
    <div>
      <div className="flex items-center gap-4 mb-4 flex-wrap">
        <span className="flex items-center">
          <span className="text-text-muted">--platform=</span>
          <LinkButton onClick={() => setPickerOpen(true)}>{platform.displayName}</LinkButton>
        </span>
        {app && platform.apps.length > 1 && (
          <span className="flex items-center">
            <span className="text-text-muted">--client=</span>
            <LinkButton onClick={() => setAppPickerOpen(true)}>
              {app.name}
              {app.featured ? "*" : ""}
            </LinkButton>
          </span>
        )}
      </div>

      {pickerOpen && (
        <div
          className="fixed inset-0 bg-black/60 flex items-center justify-center p-4"
          onClick={() => setPickerOpen(false)}
        >
          <div className="bg-bg border border-border p-4 max-w-[320px] w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex flex-col gap-3">
              {platforms.map((p) => (
                <LinkButton key={p.key} active={p.key === platform.key} onClick={() => selectPlatform(p.key)}>
                  {p.displayName}
                </LinkButton>
              ))}
            </div>
          </div>
        </div>
      )}

      {appPickerOpen && (
        <div
          className="fixed inset-0 bg-black/60 flex items-center justify-center p-4"
          onClick={() => setAppPickerOpen(false)}
        >
          <div className="bg-bg border border-border p-4 max-w-[320px] w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex flex-col gap-3">
              {platform.apps.map((a, i) => (
                <LinkButton
                  key={a.name}
                  active={i === selectedAppIndex}
                  onClick={() => {
                    setSelectedAppIndex(i);
                    setAppPickerOpen(false);
                  }}
                >
                  {a.name}
                  {a.featured ? "*" : ""}
                </LinkButton>
              ))}
            </div>
          </div>
        </div>
      )}

      {app && (
        <div className="flex flex-col gap-3">
          {app.blocks.map((block, i) => (
            <div key={i} className={`border-l-2 pl-4 ${installToneBorderClass(block.svgIconColor)}`}>
              <div className={`font-bold mb-1 ${installToneTextClass(block.svgIconColor)}`}>{block.title}</div>
              {block.description && <div className="text-text-muted mb-2">{block.description}</div>}
              <div className="flex flex-wrap gap-x-4 gap-y-2">
                {block.buttons.map((button, bi) => (
                  <LinkButton key={bi} href={button.href} target="_blank" rel="noopener noreferrer">
                    {button.text}
                  </LinkButton>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
